/**
 * UX metadata for the feature flags surfaced at /admin/feature-flags.
 *
 * Source of truth for friendly names, plain-English explanations, cost
 * impact, and dependency chains between flags. Keys MUST match the
 * backend's seed list in `src/lib/feature-flags.ts`. Unknown keys
 * (not yet documented here) fall back to the raw key + minimal info.
 */

export type FlagCost = 'none' | 'low' | 'medium' | 'high';

export interface FlagMeta {
  /** Human-readable name (used as card title). */
  friendlyName: string;
  /** One- or two-sentence description. */
  description: string;
  /** Bullet list of what happens when the flag flips on. */
  whenEnabled: string[];
  /** Cost impact tier. */
  cost: FlagCost;
  /** Short cost explanation (e.g. "~$0.06 per evaluated page"). */
  costExplain: string;
  /** Other flags that should be enabled BEFORE this one for it to do useful work. */
  dependsOn: string[];
  /** Other flags whose value depends on this being enabled (informational). */
  enables: string[];
  /** When the work fires: cron interval in seconds, or 'on-demand' / 'inline-on-write'. */
  tickCadence: string;
}

export const FLAG_META: Record<string, FlagMeta> = {
  'flag.worker.page_evaluation': {
    friendlyName: 'Page evaluation worker',
    description:
      "Background worker that evaluates individual web pages against the active GEO rule set. Each tick claims queued evaluation jobs and runs them through G-Eval (LLM-judged) and DAG (deterministic) rules.",
    whenEnabled: [
      'The /api/v1/internal/cron/page-evaluation endpoint runs every 30 seconds (in dev via the in-process scheduler; in prod via Scaleway).',
      'Each tick claims up to 5 queued PageEvaluation rows and processes them in parallel.',
      'Each rule eval makes ~1 Anthropic Sonnet call plus an OpenAI embedding for content-hash dedup.',
      'Job updates page_evaluations.aggregate_score and inserts RuleEvaluationResult rows.',
      'Submissions via POST /api/v1/brands/:id/pages/evaluations queue jobs whether the flag is on or off — but only run if it is on.',
    ],
    cost: 'medium',
    costExplain: '~$0.06 per evaluated page (Sonnet G-Eval + small embedding)',
    dependsOn: [],
    enables: ['flag.worker.sitemap_analysis'],
    tickCadence: '30s',
  },
  'flag.worker.sitemap_analysis': {
    friendlyName: 'Sitemap coverage orchestrator',
    description:
      "State-machine orchestrator that triages a brand's sitemap, runs per-page evaluations across the prioritised subset, and emits coverage scores plus content-gap recommendations.",
    whenEnabled: [
      'The /api/v1/internal/cron/process-sitemap-analysis endpoint runs every 30 seconds.',
      'Each tick advances queued/in-flight analyses through phases: fetching → triaging → evaluating → scoring → recommending.',
      'Spawns 50–500 PageEvaluation jobs per analysis (depending on Tier-3 budget). Those jobs require the Page evaluation worker flag to actually process.',
      'Computes Coverage / Quality / Topic Distribution scores at completion and writes content-gap recommendations into the Playbook system.',
      'Without the Page evaluation worker flag, analyses stall in the "evaluating" phase indefinitely.',
    ],
    cost: 'high',
    costExplain: '$3–$30 per analysis (50–500 pages × ~$0.06 page-eval cost)',
    dependsOn: ['flag.worker.page_evaluation'],
    enables: [],
    tickCadence: '30s',
  },
  'flag.worker.fanout_capture': {
    friendlyName: 'Fan-out query capture',
    description:
      'Deterministic, LLM-free extractor that scans response.tool_calls JSONB blobs and parses out observable sub-queries (web_search calls). Produces FanOutQueryObservation rows.',
    whenEnabled: [
      'The /api/v1/internal/cron/fanout-capture endpoint runs every 30 seconds.',
      'Each tick reads new responses since the last watermark, parses tool_calls, dedupes, and inserts.',
      'Zero LLM cost — pure SQL + JSON parsing.',
      'Without the Engine tool-use preservation flag, responses.tool_calls is always NULL → nothing for this worker to capture.',
    ],
    cost: 'none',
    costExplain: 'No LLM calls — DB-only',
    dependsOn: ['flag.worker.tool_use_preservation'],
    enables: ['flag.worker.fanout_synthesis'],
    tickCadence: '30s',
  },
  'flag.worker.fanout_synthesis': {
    friendlyName: 'Fan-out query synthesis',
    description:
      'Weekly LLM-heavy pipeline that synthesises additional sub-queries via 8 decomposition patterns, embeds + clusters them via pgvector, and produces queryable fan-out clusters per brand.',
    whenEnabled: [
      'The /api/v1/internal/cron/fanout-synthesis endpoint runs every 60 seconds.',
      'Each tick is a no-op unless a brand is due for re-synthesis (weekly cadence per brand).',
      'When a brand IS due, ~50–200 LLM synthesis calls + 100–500 embedding calls fire.',
      'Produces FanOutQuery and FanOutCluster rows visible at /fanout.',
      'Required input — FanOutQueryObservation rows — comes from the Fan-out query capture flag.',
    ],
    cost: 'medium',
    costExplain: '$0.50–$2 per brand per week (Sonnet synthesis + Haiku labels + embeddings)',
    dependsOn: ['flag.worker.fanout_capture'],
    enables: [],
    tickCadence: '60s',
  },
  'flag.worker.rule_ingestion': {
    friendlyName: 'Community rule ingestion',
    description:
      'Two-phase pipeline that fetches Medium GEO posts, classifies relevance via Haiku, extracts candidate rules via Sonnet, and queues them for consultant review at /admin/rules/candidates.',
    whenEnabled: [
      'Two cron endpoints fire: rule-ingestion-fetch every 120 seconds (RSS/Medium HTTP fetch) and rule-ingestion-process every 60 seconds (LLM classify + extract).',
      'Fetch is HTTP-only; processing makes ~1–2 LLM calls per relevant post (Haiku classifier always; Sonnet extractor only when classified relevant).',
      'Approved candidates flow into the Rule library with status=approved_inactive — admins still need to manually flip them to active.',
    ],
    cost: 'low',
    costExplain: '~$0.01–$0.05 per processed post',
    dependsOn: [],
    enables: [],
    tickCadence: 'fetch 120s · process 60s',
  },
  'flag.worker.tool_use_preservation': {
    friendlyName: 'Engine tool-use preservation',
    description:
      'Widens the ChatGPT and Claude engine adapters to preserve tool-use metadata (web_search calls, etc.) into the responses.tool_calls JSONB column. Required input for fan-out capture.',
    whenEnabled: [
      'Engine adapters include tool definitions in API calls when QueryParams.tools.webSearch is opt-in.',
      'OpenAI / Anthropic charge for tool-use round-trips: ~$0.50 per query that triggers web_search, $0 when it does not.',
      'A tool_calls JSONB blob is persisted alongside each response text body.',
      'Acts as a kill-switch for cost regression — disable to halt all opt-in web-search billing if provider pricing changes mid-month.',
    ],
    cost: 'high',
    costExplain: '~$0.50 per query that triggers a web_search round-trip (only fires when QueryParams.tools.webSearch is opted-in)',
    dependsOn: [],
    enables: ['flag.worker.fanout_capture'],
    tickCadence: 'inline on each query write',
  },
  'feature.personas': {
    friendlyName: 'Persona builder',
    description:
      'Client-facing feature that unlocks the Persona builder admin surface and the persona-fit pages. When off, persona routes are hidden and persona-fit evaluation cannot be requested.',
    whenEnabled: [
      'The Personas admin surface becomes available for defining audience personas per brand.',
      'Persona-fit evaluation pages (response and page persona-fit, cross-lens diagnoses) unlock.',
      'No background cost on its own — it gates UI access; the persona worker flags below control the actual evaluation spend.',
    ],
    cost: 'none',
    costExplain: 'No LLM calls — UI/feature gate only',
    dependsOn: [],
    enables: [
      'flag.worker.persona_response_eval',
      'flag.worker.page_persona_evaluation',
      'flag.worker.persona_diagnosis',
    ],
    tickCadence: 'feature gate (no tick)',
  },
  'flag.worker.persona_response_eval': {
    friendlyName: 'AI-answer persona-fit worker',
    description:
      'Background worker that scores captured AI responses against each brand persona, producing persona-fit findings that feed the cross-lens diagnosis.',
    whenEnabled: [
      'The response persona-fit cron tick runs and claims queued evaluation jobs (no-op when off).',
      'Each evaluated finding makes ~1 LLM call to judge persona alignment of a response.',
      'Findings surface on the persona-fit pages and become inputs to the Persona diagnosis orchestrator.',
    ],
    cost: 'medium',
    costExplain: '~1 LLM call per evaluated response finding',
    dependsOn: ['feature.personas'],
    enables: ['flag.worker.persona_diagnosis'],
    tickCadence: 'cron tick',
  },
  'flag.worker.page_persona_evaluation': {
    friendlyName: 'Page persona-fit worker',
    description:
      'Background worker that evaluates individual web pages against each brand persona across the persona dimensions, producing per-page persona-fit scores.',
    whenEnabled: [
      'The page persona-fit cron tick runs and claims queued page-evaluation jobs (no-op when off).',
      'Each evaluated page makes ~1 LLM call to score the page against the persona dimensions.',
      'Per-page persona-fit results surface on the page persona-fit page and feed the cross-lens diagnosis.',
    ],
    cost: 'medium',
    costExplain: '~1 LLM call per evaluated page',
    dependsOn: ['feature.personas'],
    enables: ['flag.worker.persona_diagnosis'],
    tickCadence: 'cron tick',
  },
  'flag.worker.persona_diagnosis': {
    friendlyName: 'Persona diagnosis orchestrator',
    description:
      'Cross-lens orchestrator that synthesises response and page persona-fit findings into a single per-persona diagnosis narrative plus prioritised recommendations.',
    whenEnabled: [
      'The persona-diagnosis cron tick runs and advances queued diagnoses (no-op when off).',
      'Each diagnosis makes several LLM calls to synthesise a narrative and recommendations across the response + page lenses.',
      'Without the response and page persona-fit workers, there are no findings to diagnose.',
    ],
    cost: 'medium',
    costExplain: 'Several LLM calls per diagnosis (narrative + recommendations synthesis)',
    dependsOn: [
      'flag.worker.persona_response_eval',
      'flag.worker.page_persona_evaluation',
    ],
    enables: [],
    tickCadence: 'cron tick',
  },
  'flag.subsystem.search_console': {
    friendlyName: 'Google Search Console sync',
    description:
      'Kill-switch for the Google Search Console integration: OAuth connection plus the per-brand daily sync of first-party search performance data.',
    whenEnabled: [
      'Search Console OAuth and the /search-console/* routes become available (they return 503 when off).',
      'The daily per-brand sync worker pulls Search Console performance data (no-op when off).',
      'No LLM cost — Google API calls plus database writes only.',
    ],
    cost: 'none',
    costExplain: 'No LLM calls — Google API + DB only',
    dependsOn: [],
    enables: [],
    tickCadence: 'daily per-brand sync',
  },
  'flag.recommendations.llm_generation': {
    friendlyName: 'LLM-generated recommendations',
    description:
      'Controls whether content-gap recommendations from sitemap analyses are LLM-generated (Sonnet) or built from deterministic templates.',
    whenEnabled: [
      'Each completed sitemap analysis spawns ~5–15 Sonnet calls during the recommending phase.',
      'LLM writes a personalised paragraph + content-type pick per uncovered cluster.',
      'When off, recommendations come from a static template per priority/gap_type combo — useful for cost-sensitive runs.',
      'Affects only the recommending phase — flipping mid-analysis does not retroactively change earlier phases.',
    ],
    cost: 'medium',
    costExplain: '~$0.10–$0.30 per sitemap analysis depending on uncovered cluster count',
    dependsOn: ['flag.worker.sitemap_analysis'],
    enables: [],
    tickCadence: 'on-demand (during the recommending phase)',
  },
  'flag.collection.grounded_default': {
    friendlyName: 'Grounded capture by default',
    description:
      "Makes web-search grounding the default for everyday collection. When on, engines that support it run a live web search while answering, so captured responses cite real sources instead of relying only on the model's training data.",
    whenEnabled: [
      'New collection queries ask supported engines (ChatGPT, Claude) to run a live web search while answering.',
      'Captured responses carry the sources the engine actually cited — the raw input for source intelligence and fan-out analysis.',
      'Each grounded query costs more than an ungrounded one because it adds a web-search round-trip on the provider side.',
      'Requires the Engine tool-use preservation master switch — with that off, nothing can ground regardless of this setting.',
      'When off, grounding can still be turned on for a single pilot client; this switch turns it on for everyone.',
    ],
    cost: 'high',
    costExplain:
      'Adds a web-search round-trip to every supported collection query (~$0.50 per grounded query that searches)',
    dependsOn: ['flag.worker.tool_use_preservation'],
    enables: [],
    tickCadence: 'inline on each collection query',
  },
  'flag.collection.region_capture': {
    friendlyName: 'Per-region capture',
    description:
      'Captures AI answers separately for each region a brand monitors, so you can see how (for example) a US user and a Danish user get different responses — side by side, never blended. Applies only to brands set up with two or more monitored regions.',
    whenEnabled: [
      'For a brand with two or more monitored regions, collection runs each query once per region, passing the region to every engine that can honour it.',
      'Each captured response is labelled with how faithfully the region was applied — truly applied, approximated, or not controllable for that engine — so a regional result is never overstated.',
      'A brand with a single region (or none added) is unaffected and captures exactly as before.',
      'Multiplies collection volume by the number of monitored regions, so enable it alongside the regional sampling controls to keep cost in check.',
    ],
    cost: 'high',
    costExplain: 'Multiplies collection cost by the number of monitored regions on each brand',
    dependsOn: [],
    enables: [],
    tickCadence: 'inline on each collection cycle',
  },
  'flag.worker.fanout_rebuild': {
    friendlyName: 'Fan-out clean rebuild',
    description:
      "Lets an admin rebuild a single brand's fan-out query set from scratch — clearing the previously inferred queries and clusters and re-synthesising them cleanly. Used to apply improved de-duplication and clustering to a brand whose fan-out was built by an older version.",
    whenEnabled: [
      'The per-brand "rebuild fan-out" admin action becomes live (it does nothing while this is off).',
      "Triggering a rebuild clears the brand's inferred fan-out queries and clusters, then re-runs synthesis from the observed sub-queries.",
      'The rebuild is carried out by the Fan-out query synthesis worker, so that flag must also be on for a rebuild to finish.',
      'A cost estimate (dry run) is always available and does not need this switch.',
    ],
    cost: 'medium',
    costExplain:
      'Re-runs fan-out synthesis for each rebuilt brand ($0.50–$2 per brand, same as a weekly synthesis pass)',
    dependsOn: ['flag.worker.fanout_synthesis'],
    enables: [],
    tickCadence: 'on-demand (admin) + cron drain',
  },
};

/**
 * Per-engine provider kill switches (`flag.provider.<engine>.disabled`).
 *
 * Inverted semantics vs every other flag: ON means the engine is *disabled*.
 * The switch normally sits OFF (the engine is collected). It flips ON
 * automatically when the provider rejects GEO Observatory's API key — so jobs fail fast
 * instead of hammering a known-bad key — and an admin turns it back OFF once the
 * key is fixed. Generated for each engine so the card reads in plain language.
 */
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  google_aio: 'Google AI Overviews',
  mistral: 'Mistral',
};

function providerKillSwitchMeta(displayName: string): FlagMeta {
  return {
    friendlyName: `${displayName} — provider kill switch`,
    description:
      `Pauses all new queries to ${displayName} when on. This switch is normally off (${displayName} answers are collected). It turns on automatically if ${displayName} rejects GEO Observatory's API key, so jobs fail fast instead of retrying against a bad key — and an admin turns it back off once the key is fixed.`,
    whenEnabled: [
      `New queries to ${displayName} are skipped immediately and marked as a provider outage, instead of calling the API.`,
      `This guards against retry storms and account lockouts while the ${displayName} key is invalid or the provider is down.`,
      `Existing ${displayName} results stay visible — only new collection from ${displayName} pauses.`,
      `Turn this off to resume collecting ${displayName} answers once the key or outage is resolved.`,
    ],
    cost: 'none',
    costExplain: 'No cost — a kill switch that pauses spend on this provider',
    dependsOn: [],
    enables: [],
    tickCadence: 'auto on API-key failure · manual re-enable',
  };
}

for (const [engine, displayName] of Object.entries(PROVIDER_DISPLAY_NAMES)) {
  FLAG_META[`flag.provider.${engine}.disabled`] = providerKillSwitchMeta(displayName);
}

/**
 * Returns metadata for a key, or a permissive fallback for keys that
 * have not been documented yet.
 */
export function getFlagMeta(key: string): FlagMeta {
  const known = FLAG_META[key];
  if (known) return known;
  // Defensive: any provider kill switch not pre-generated (e.g. a new engine)
  // still reads in plain language rather than "No documentation yet."
  const providerMatch = /^flag\.provider\.(.+)\.disabled$/.exec(key);
  if (providerMatch) {
    const engine = providerMatch[1];
    const display = PROVIDER_DISPLAY_NAMES[engine] ?? engine;
    return providerKillSwitchMeta(display);
  }
  return {
    friendlyName: key,
    description: 'No documentation yet. Toggle with care.',
    whenEnabled: [],
    cost: 'low',
    costExplain: 'Unknown',
    dependsOn: [],
    enables: [],
    tickCadence: 'unknown',
  };
}

// Spec 235 task 3 — the chip-tone mapping (evidence.md): blue/sky -> info,
// amber -> warn, rose -> fail. `tone: undefined` renders a bare neutral
// Chip ("no cost" is not a status worth colouring).
export type ChipTone = 'ok' | 'info' | 'warn' | 'fail' | undefined;

const COST_LABELS: Record<FlagCost, { label: string; tone: ChipTone }> = {
  none: { label: 'No cost', tone: undefined },
  low: { label: 'Low cost', tone: 'info' },
  medium: { label: 'Medium cost', tone: 'warn' },
  high: { label: 'High cost', tone: 'fail' },
};

export function costPillStyle(cost: FlagCost): { label: string; tone: ChipTone } {
  return COST_LABELS[cost];
}
